import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Search, ArrowUpRight, BookOpen, CreditCard, Users, Brain, CalendarDays,
  FileText, Zap, BarChart3, Star, Settings2, Plug, ShieldCheck, Sparkles,
  ChevronRight,
} from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { getHelpTree, type HelpCategory } from "@/lib/help.functions";
import { alternateLinkList } from "@/lib/i18n/locale";

export const Route = createFileRoute("/vi/help/")({
  loader: () => getHelpTree({ data: { locale: "vi" } }),
  head: () => {
    const url = "https://banana.cards/vi/help";
    const title = "Trung tâm Hỗ trợ — banana.cards";
    const description = "Hướng dẫn, tutorial và câu trả lời cho Banana Cards. Tìm hiểu cách dùng thẻ kỹ thuật số, CRM liên hệ, AI brain, đặt lịch, hợp đồng, tích hợp và nhiều hơn.";
    const collectionLd = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Trung tâm Hỗ trợ",
      description,
      url,
      inLanguage: "vi",
      isPartOf: { "@type": "WebSite", name: "Banana Cards", url: "https://banana.cards" },
    };
    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Trang chủ", item: "https://banana.cards/vi" },
        { "@type": "ListItem", position: 2, name: "Trung tâm Hỗ trợ", item: url },
      ],
    };
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: "Mọi thứ bạn cần để tận dụng tối đa Banana Cards." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "Banana Cards" },
        { property: "og:locale", content: "vi_VN" },
        { property: "og:locale:alternate", content: "en" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [
        { rel: "canonical", href: url },
        ...alternateLinkList("/help", "vi"),
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(collectionLd) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbLd) },
      ],
    };
  },
  component: HelpIndexVi,
});

const ICONS: Record<string, any> = {
  "getting-started": Sparkles,
  cards: CreditCard,
  contacts: Users,
  ai: Brain,
  bookings: CalendarDays,
  contracts: FileText,
  automations: Zap,
  analytics: BarChart3,
  reviews: Star,
  team: Settings2,
  integrations: Plug,
  account: ShieldCheck,
  default: BookOpen,
};

const iconFor = (slug: string) => ICONS[slug] ?? ICONS.default;
const LOCALE = "vi";
const viHelpHref = (segs: string[]) => "/vi/help/" + segs.join("/");

function HelpIndexVi() {
  const initialData = Route.useLoaderData() as { categories: HelpCategory[]; articles: Array<{ id: string; slug: string; category_id: string; updated_at: string; title: string; excerpt: string | null }> };
  const fetchTree = useServerFn(getHelpTree);
  const { data, isLoading } = useQuery({
    queryKey: ["help-tree", LOCALE],
    queryFn: () => fetchTree({ data: { locale: LOCALE } }),
    initialData,
    staleTime: 60_000,
  });
  const [q, setQ] = useState("");

  const roots = useMemo<HelpCategory[]>(
    () => (data?.categories ?? []).filter((c) => c.parent_id === null),
    [data],
  );
  const childrenOf = (id: string) =>
    (data?.categories ?? []).filter((c) => c.parent_id === id);
  const articles = data?.articles ?? [];

  const filteredArticles = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [] as typeof articles;
    return articles
      .filter((a: typeof articles[number]) =>
        a.title.toLowerCase().includes(term) || (a.excerpt ?? "").toLowerCase().includes(term))
      .slice(0, 8);
  }, [q, articles]);

  const articlePath = (a: { category_id: string; slug: string }) => {
    const cats = data?.categories ?? [];
    const path: string[] = [];
    let cur = cats.find((c) => c.id === a.category_id);
    while (cur) {
      path.unshift(cur.slug);
      cur = cur.parent_id ? cats.find((c) => c.id === cur!.parent_id) : undefined;
    }
    return viHelpHref([...path, a.slug]);
  };

  return (
    <SiteLayout>
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 pb-12 pt-20 text-center md:px-6">
          <p className="inline-flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-lime" />
            Trung tâm Hỗ trợ
          </p>
          <h1 className="mt-4 font-display text-4xl leading-[1.05] md:text-6xl">
            Chúng tôi có thể <em className="italic">giúp gì?</em>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">
            Tìm kiếm hướng dẫn, tutorial và hướng dẫn từng bước cho mọi ngóc ngách của Banana Cards.
          </p>

          <div className="relative mx-auto mt-8 max-w-2xl">
            <Search className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Tìm bài viết, ví dụ 'chia sẻ thẻ của tôi'"
              className="w-full rounded-xl bg-muted px-14 py-5 text-base outline-none transition focus:bg-card focus:ring-2 focus:ring-foreground"
            />
            {filteredArticles.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-10 mt-2 overflow-hidden rounded-2xl bg-card text-left">
                {filteredArticles.map((a: typeof articles[number]) => (
                  <Link
                    key={a.id}
                    to={articlePath(a) as any}
                    className="flex items-start justify-between gap-3 px-5 py-3 transition hover:bg-muted"
                  >
                    <div>
                      <div className="text-sm font-bold">{a.title}</div>
                      {a.excerpt && <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{a.excerpt}</div>}
                    </div>
                    <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="bg-muted">
        <div className="mx-auto max-w-6xl px-4 py-16 md:px-6">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Duyệt theo chủ đề</p>
              <h2 className="mt-2 font-display text-3xl md:text-4xl">Chọn một <em className="italic">danh mục</em>.</h2>
            </div>
          </div>

          {isLoading ? (
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-44 animate-pulse rounded-2xl bg-card" />
              ))}
            </div>
          ) : roots.length === 0 ? (
            <div className="mt-8 rounded-2xl bg-card p-10 text-center">
              <p className="font-display text-xl">Chưa có bài viết nào.</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Chạy script SQL của trung tâm hỗ trợ để tạo bảng và seed bài viết đầu tiên.
              </p>
            </div>
          ) : (
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {roots.map((cat) => {
                const Icon = iconFor(cat.slug);
                const subs = childrenOf(cat.id);
                const catArticles = articles.filter((a: typeof articles[number]) => a.category_id === cat.id);
                return (
                  <div key={cat.id} className="group flex h-full flex-col rounded-2xl bg-card p-6 transition hover:-translate-y-0.5">
                    <Link to={viHelpHref([cat.slug]) as any} className="flex items-start justify-between">
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-50 text-foreground">
                        <Icon className="h-5 w-5" />
                      </div>
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground transition group-hover:rotate-12" />
                    </Link>
                    <Link to={viHelpHref([cat.slug]) as any} className="mt-5 block">
                      <h3 className="font-display text-xl transition hover:underline">{cat.name}</h3>
                      {cat.description && (
                        <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">{cat.description}</p>
                      )}
                    </Link>
                    {subs.length > 0 ? (
                      <ul className="mt-5 space-y-1.5 text-sm">
                        {subs.slice(0, 4).map((s) => (
                          <li key={s.id}>
                            <Link
                              to={viHelpHref([cat.slug, s.slug]) as any}
                              className="flex items-center gap-1.5 text-foreground/80 transition hover:text-foreground hover:underline"
                            >
                              <ChevronRight className="h-3.5 w-3.5 opacity-50" />
                              {s.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ) : catArticles.length > 0 ? (
                      <ul className="mt-5 space-y-1.5 text-sm">
                        {catArticles.slice(0, 4).map((a: typeof articles[number]) => (
                          <li key={a.id}>
                            <Link
                              to={viHelpHref([cat.slug, a.slug]) as any}
                              className="flex items-center gap-1.5 text-foreground/80 transition hover:text-foreground hover:underline"
                            >
                              <ChevronRight className="h-3.5 w-3.5 opacity-50" />
                              {a.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    <div className="mt-auto pt-5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                      {cat.article_count ?? 0} bài viết
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 py-16 md:px-6">
          <div className="rounded-3xl bg-muted p-8 md:p-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Vẫn chưa rõ?</p>
            <h3 className="mt-2 font-display text-2xl md:text-3xl">
              Đội ngũ chúng tôi chỉ cách bạn <em className="italic">một tin nhắn.</em>
            </h3>
            <p className="mt-3 max-w-xl text-sm text-muted-foreground">
              Không tìm thấy điều bạn cần? Liên hệ — chúng tôi đọc mọi tin nhắn.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/vi/contact" className="inline-flex items-center gap-2 rounded-xl bg-foreground px-5 py-3 text-sm font-bold text-background transition hover:opacity-90">
                Liên hệ hỗ trợ <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a href="mailto:hello@banana.cards" className="inline-flex items-center gap-2 rounded-xl bg-card px-5 py-3 text-sm font-bold transition hover:bg-foreground hover:text-background">
                hello@banana.cards
              </a>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}